import { useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Sparkles } from "lucide-react";
import { useServices, useUpdateService, type ServiceRow } from "@/hooks/admin/useServices";
import { MediaPicker } from "@/components/media/MediaPicker";

export default function ServicesAdminPage() {
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const { data: services = [], isLoading } = useServices();

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
            <Sparkles className="h-6 w-6" /> Services
          </h1>
          <p className="text-sm text-muted-foreground">
            First-class service-type entries surfaced on the public TC site (weddings, corporate, etc.). Edit copy + images here without a deploy.
          </p>
        </div>

        <Card>
          <CardContent className="pt-5">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Order</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
                  {!isLoading && services.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground text-center py-8">No services configured.</TableCell></TableRow>
                  )}
                  {services.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">{s.sort_order}</TableCell>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">/{s.slug}</TableCell>
                      <TableCell>
                        <Badge variant={s.is_active ? "default" : "secondary"} className="text-xs">
                          {s.is_active ? "Active" : "Hidden"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(s)} aria-label={`Edit ${s.name}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <ServiceEditor service={editing} onClose={() => setEditing(null)} />
    </DashboardLayout>
  );
}

interface FormState {
  name: string;
  slug: string;
  short_description: string;
  long_description_html: string;
  hero_image_url: string;
  sort_order: number;
  is_active: boolean;
}

function ServiceEditor({ service, onClose }: { service: ServiceRow | null; onClose: () => void }) {
  const update = useUpdateService();
  const [form, setForm] = useState<FormState | null>(null);

  useMemo(() => {
    if (!service) { setForm(null); return; }
    setForm({
      name: service.name,
      slug: service.slug,
      short_description: service.short_description ?? "",
      long_description_html: service.long_description_html ?? "",
      hero_image_url: service.hero_image_url ?? "",
      sort_order: service.sort_order,
      is_active: service.is_active,
    });
  }, [service]);

  if (!service || !form) return null;

  const handleSave = async () => {
    await update.mutateAsync({
      id: service.id,
      name: form.name.trim(),
      short_description: form.short_description.trim() || null,
      long_description_html: form.long_description_html.trim() || null,
      hero_image_url: form.hero_image_url.trim() || null,
      sort_order: form.sort_order,
      is_active: form.is_active,
    });
    onClose();
  };

  return (
    <Sheet open={!!service} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>/{form.slug}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Short description</Label>
            <Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Long description (HTML)</Label>
            <Textarea rows={6} value={form.long_description_html} onChange={(e) => setForm({ ...form, long_description_html: e.target.value })} placeholder="<p>…</p>" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Hero image</Label>
            <MediaPicker
              value={form.hero_image_url}
              onChange={(url) => setForm({ ...form, hero_image_url: url })}
              folder="services"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Sort order</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex items-end justify-between pb-2">
              <Label className="text-xs">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            </div>
          </div>
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={update.isPending}>Cancel</Button>
          <Button onClick={handleSave} disabled={update.isPending || !form.name.trim()}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
